// The browser half of a push subscription, with no dependency on the API client — so the API
// client itself can use it when an account turns out to be gone (see lib/api.ts).

// currentPushSubscription returns this browser's push subscription, if any. getRegistration
// resolves at once (undefined without a service worker), unlike serviceWorker.ready, which would
// wait forever in dev mode.
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    return (await registration?.pushManager?.getSubscription()) ?? null
  } catch {
    return null
  }
}

// dropThisBrowserPush unsubscribes this browser. Enough on its own to stop delivery: the push
// service then answers 410 and the backend prunes the row on its next send. beforeUnsubscribe gets
// the endpoint first, for telling the server while the subscription still exists. Capped, so
// signing out never waits on a slow browser or network.
export async function dropThisBrowserPush(
  beforeUnsubscribe?: (endpoint: string) => Promise<void>,
  timeoutMs = 2500,
): Promise<void> {
  const work = (async () => {
    const subscription = await currentPushSubscription()
    if (!subscription) return
    if (beforeUnsubscribe) await beforeUnsubscribe(subscription.endpoint).catch(() => {})
    await subscription.unsubscribe().catch(() => false)
  })()
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, timeoutMs)
  })
  await Promise.race([work.catch(() => {}), timeout])
}
